import React, { useState } from 'react';
import { Table, EllipsisDropdown, ButtonLink, ControlledPopup, Notification, STATUS_TYPES, USER_PERMISSIONS } from 'dyl-components';
import { Dropdown, Icon, Header } from 'semantic-ui-react';

import { Person } from 'dyl-components';
import EntriesModal from './EntriesModal';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import webformsActions from 'actions/webforms';
import RestrictedView from 'shared/RestrictedView';

const WebFormRow = ({ webform, limit }) => {
    const [isEntriesModalOpen, setModal] = useState(false);
    const navigate = useNavigate();
    const webforms = useSelector(state => state.webforms.webforms) || [];

    const info = {
        name: webform.internalName,
        createdOn: webform.createdOn,
        lastEdited: webform.lastEdited,
        entries: webform.entries,
        opportunities: webform.opportunities,
        customers: webform.customers
    }

    const dispatch = useDispatch();

    const [params] = useSearchParams();
    const refresh = () => {
        dispatch(webformsActions.readForms({
            ...Object.fromEntries(params),
            limit
        }));
    }

    const ELLIPSIS_OPTIONS = [
        { text: 'Edit', icon: 'fas fa-pencil', as: Link, to: `/settings/web-forms/edit/${webform.id}`, userPermission: USER_PERMISSIONS.WEB_FORMS_EDIT },
        {
            text: 'Duplicate', icon: 'fas fa-copy', as: Link, to: '/settings/web-forms/create', state: {
                form_id: webform.id,
                breadcrumb_header: `Duplicate '${webform.internalName}'`
            },
            userPermission: USER_PERMISSIONS.WEB_FORMS_EDIT,
        },
        ...(webform.status === 'Active' ? [{
            text: 'Deactivate',
            icon: 'fas fa-eye-slash',
            userPermission: USER_PERMISSIONS.WEB_FORMS_EDIT,
            displaysPopup: true,
            popupProps: {
                header: 'Are you sure?',
                subheader: 'Deactivating will disable the shared links and unpublish this form.',
                onConfirm: async () => {
                    try {
                        await dispatch(webformsActions.updateStatus(webform.id, { active: false }));
                        Notification.alert(`Successfully deactivated webform!`, STATUS_TYPES.SUCCESS);
                        refresh();
                    } catch (e) {
                        console.log(e);
                        Notification.alert(`Failed to deactivate webform`, STATUS_TYPES.ERROR);
                    }
                }
            }
        }] : [{
            text: 'Activate',
            icon: 'fas fa-eye',
            userPermission: USER_PERMISSIONS.WEB_FORMS_EDIT,
            onClick: async () => {
                try {
                    await dispatch(webformsActions.updateStatus(webform.id, { active: true }));
                    Notification.alert(`Successfully activated webform!`, STATUS_TYPES.SUCCESS);
                    refresh();
                } catch (e) {
                    console.log(e);
                    Notification.alert(`Failed to activate webform`, STATUS_TYPES.ERROR);
                }
            }
        }]),
        {
            text: 'Delete',
            icon: 'fas fa-trash-alt',
            userPermission: USER_PERMISSIONS.WEB_FORMS_DELETE,
            displaysPopup: true,
            popupProps: {
                header: 'Are you sure?',
                onConfirm: async () => {
                    try {
                        await dispatch(webformsActions.deleteForm(webform.id));
                        Notification.alert('Successfully deleted webform!', STATUS_TYPES.SUCCESS, true);
                        const isNowEmpty = webforms.length - 1 === 0;
                        if (!isNowEmpty) {
                            refresh();
                        } else {
                            const query = new URLSearchParams(params);
                            const page = query.get('page');
                            if (Number(page) !== 1) {
                                query.set('page', page - 1);
                                const query_string = query.toString();
                                navigate(`/settings/web-forms${query_string ? `?${query_string}` : ''}`);
                            } else {
                                refresh();
                            }
                        }
                    } catch (e) {
                        console.log(e);
                        Notification.alert('Failed to delete webform', STATUS_TYPES.ERROR, true);
                    }
                }
            }
        }
    ];
    

    const DropdownModel = ({ text, icon, userPermission, ...otherProps }) => (
        <RestrictedView userPermission={userPermission} type="disabled">
            <Dropdown.Item
                text={
                    <div>
                        <Icon name={icon} />
                        {text}
                    </div>
                }
                style={{ cursor: "pointer" }}
                {...otherProps}
            />
        </RestrictedView>
    );

    const DropdownItem = ({ text, icon, displaysPopup, popupProps, userPermission, ...otherProps }) => {
            return (
                <RestrictedView type='controlledPopupDisabled' userPermission={userPermission}>
                <ControlledPopup
                    header={
                        <Header as='h4' textAlign='center'>
                            {popupProps.header}
                            <Header.Subheader style={{ marginTop: 5 }}>
                                {popupProps.subheader}
                            </Header.Subheader>
                        </Header>
                    }
                    trigger={
                        <DropdownModel icon={icon} text={text} userPermission={userPermission} {...otherProps}/>
                    }
                    onConfirm={popupProps.onConfirm}
                />
                </RestrictedView>
            )
    }

    return (
        <React.Fragment>
            <Table.Row>
                <Table.Cell width={3}>
                    <Link to={`/settings/web-forms/edit/${webform.id}`}>
                        {webform.internalName}
                    </Link>
                </Table.Cell>
                <Table.Cell width={3}>
                    {webform.displayName}
                </Table.Cell>
                <Table.Cell width={3}>
                    <Person username={webform.created.name} email={webform.created.email} isInactiveUser={webform.created.active === false} />
                </Table.Cell>

                <Table.Cell width={1}>
                    <ButtonLink onClick={() => { setModal(true) }}>
                        {webform.entries}
                    </ButtonLink>
                </Table.Cell>
                <Table.Cell width={2}>
                    {webform.status}
                </Table.Cell>
                <Table.Cell width={1}>
                    <EllipsisDropdown>
                        {ELLIPSIS_OPTIONS.map(({ text, icon, displaysPopup, popupProps, ...otherProps }) => {
                            if(displaysPopup){
                                return (
                                    <DropdownItem
                                    text={text}
                                    icon={icon}
                                    displaysPopup={displaysPopup}
                                    popupProps={popupProps}
                                    {...otherProps}
                                    />
                                );
                            }
                            return <DropdownModel icon={icon} text={text} userPermission={otherProps?.userPermission} {...otherProps}/>
                        })}
                        
                    </EllipsisDropdown>
                </Table.Cell>
            </Table.Row>
            {isEntriesModalOpen &&
                <EntriesModal info={info} open={isEntriesModalOpen} onClose={() => { setModal(false) }} />
            }
        </React.Fragment>
    )
}

export default WebFormRow;
